import { setClubCatalog } from "@clubap/shared";
import type {
  ClubAnnouncementDto,
  ClubDef,
  ClubId,
  ClubProposalDto,
  ClubProposalInput,
  ClubRequestDto,
  ClubRosterDto,
  ClubSummary,
  ConversationPage,
  DirectMessageDto,
  FriendSummary,
  SearchResult,
  SendRequestResult,
  SocialSnapshot,
} from "@clubap/shared";
import { authedJson, authedRequest } from "../api.js";
import { API_URL } from "../backend.js";

/**
 * Cliente de `/api/social`. Todo lo duradero pasa por acá: los avisos del canal en
 * vivo sólo evitan tener que volver a pedir el snapshot.
 */

/**
 * La URL del logo de un club, con su versión pegada.
 *
 * Sin sesión, y a propósito: esto lo consume un `<img>`, que no puede mandar la cabecera
 * de autorización. No se filtra nada por eso — el catálogo de clubes ya lo ve cualquiera
 * que entre, y el logo es su cara.
 *
 * La versión en la query es lo que hace que el navegador pueda cachear el archivo para
 * siempre y aun así soltarlo el día que el presidente lo cambia: la URL cambia, la caché
 * no tiene que adivinar.
 */
export function clubLogoUrl(club: { id: ClubId; logoVersion: number }): string {
  return `${API_URL}/api/clubs/${encodeURIComponent(club.id)}/logo?v=${club.logoVersion}`;
}


/**
 * El catálogo de clubes, y el registro que lo recibe.
 *
 * Se pide UNA vez al entrar y se vuelve a pedir cuando algo lo cambia —se funda un club,
 * un presidente cambia su logo— porque `getClub()` en el cliente lee un registro en
 * memoria: hasta que alguien lo llene, este proceso cree que no existe ningún club y las
 * insignias salen en blanco.
 *
 * No trae los logos: son hasta 64 KB por club. Trae la versión de cada uno, y la imagen
 * se pide por su URL cuando de verdad hay que dibujarla.
 */
export async function loadClubCatalog(): Promise<ClubDef[]> {
  const { clubs } = await authedJson<{ clubs: ClubDef[] }>("/api/clubs");
  setClubCatalog(clubs);
  return clubs;
}

export function proposeClub(input: ClubProposalInput): Promise<ClubProposalDto> {
  return authedJson<{ proposal: ClubProposalDto }>("/api/clubs/proposals", {
    method: "POST",
    body: JSON.stringify(input),
  }).then((result) => result.proposal);
}

export function fetchMyProposal(): Promise<ClubProposalDto | null> {
  return authedJson<{ proposal: ClubProposalDto | null }>("/api/clubs/proposals/mine").then(
    (result) => result.proposal,
  );
}

export function withdrawProposal(id: string): Promise<void> {
  return authedJson<{ ok: true }>(`/api/clubs/proposals/${encodeURIComponent(id)}/withdraw`, {
    method: "POST",
  }).then(() => undefined);
}

export function fetchPendingProposals(): Promise<ClubProposalDto[]> {
  return authedJson<{ proposals: ClubProposalDto[] }>("/api/clubs/proposals/pending").then(
    (result) => result.proposals,
  );
}

/** Aprobar devuelve el catálogo nuevo: el club acaba de nacer y hay que conocerlo ya. */
export async function approveProposal(id: string): Promise<ClubDef[]> {
  const { clubs } = await authedJson<{ clubs: ClubDef[] }>(
    `/api/clubs/proposals/${encodeURIComponent(id)}/approve`,
    { method: "POST" },
  );
  setClubCatalog(clubs);
  return clubs;
}

export function rejectProposal(id: string): Promise<ClubProposalDto[]> {
  return authedJson<{ proposals: ClubProposalDto[] }>(
    `/api/clubs/proposals/${encodeURIComponent(id)}/reject`,
    { method: "POST" },
  ).then((result) => result.proposals);
}

export async function uploadClubLogo(
  clubId: ClubId,
  logo: string,
  logoType: string,
): Promise<ClubDef[]> {
  const { clubs } = await authedJson<{ logoVersion: number; clubs: ClubDef[] }>(
    `/api/clubs/${encodeURIComponent(clubId)}/logo`,
    { method: "POST", body: JSON.stringify({ logo, logoType }) },
  );
  setClubCatalog(clubs);
  return clubs;
}

export function fetchSocialSnapshot(): Promise<SocialSnapshot> {
  return authedJson<SocialSnapshot>("/api/social");
}

export function searchPlayers(query: string): Promise<SearchResult[]> {
  return authedJson<{ results: SearchResult[] }>(
    `/api/social/search?q=${encodeURIComponent(query)}`,
  ).then((data) => data.results);
}

export function sendFriendRequest(accountId: string): Promise<SendRequestResult> {
  return authedJson<SendRequestResult>("/api/social/requests", { to: accountId });
}

export function acceptFriendRequest(requestId: string): Promise<FriendSummary> {
  return authedJson<{ friend: FriendSummary }>(
    `/api/social/requests/${encodeURIComponent(requestId)}/accept`,
    {},
  ).then((data) => data.friend);
}

export function rejectFriendRequest(requestId: string): Promise<void> {
  return authedRequest(`/api/social/requests/${encodeURIComponent(requestId)}/reject`, {});
}

export function cancelFriendRequest(requestId: string): Promise<void> {
  return authedRequest(`/api/social/requests/${encodeURIComponent(requestId)}/cancel`, {});
}

export function removeFriend(accountId: string): Promise<void> {
  return authedRequest(`/api/social/friends/${encodeURIComponent(accountId)}/remove`, {});
}

export function fetchConversation(friendId: string, before?: string): Promise<ConversationPage> {
  const suffix = before ? `?before=${encodeURIComponent(before)}` : "";
  return authedJson<ConversationPage>(`/api/social/dm/${encodeURIComponent(friendId)}${suffix}`);
}

export function sendDirectMessage(friendId: string, text: string): Promise<DirectMessageDto> {
  return authedJson<{ message: DirectMessageDto }>(
    `/api/social/dm/${encodeURIComponent(friendId)}`,
    { text },
  ).then((data) => data.message);
}

export function markConversationRead(friendId: string): Promise<number> {
  return authedJson<{ read: number }>(
    `/api/social/dm/${encodeURIComponent(friendId)}/read`,
    {},
  ).then((data) => data.read);
}

export interface ClubsView {
  catalog: ClubDef[];
  clubs: ClubSummary[];
  mine: ClubId[];
}

export function fetchClubs(): Promise<ClubsView> {
  return authedJson<ClubsView>("/api/social/clubs");
}

export function fetchClubRoster(clubId: string): Promise<ClubRosterDto> {
  return authedJson<ClubRosterDto>(`/api/social/clubs/${encodeURIComponent(clubId)}/roster`);
}

/**
 * Entrar y salir devuelven el resumen COMPLETO de clubes, no sólo los míos.
 *
 * Es lo que permite que la pestaña que actúa se actualice sola: si dependiera del aviso
 * del canal social, con el socket caído la petición saldría bien y la pantalla se
 * quedaría igual.
 */
export interface ClubChange {
  mine: ClubId[];
  clubs: ClubSummary[];
}

/** Pedir entrada. `joined` si el club estaba acéfalo, `pending` si hay presidente. */
export interface ClubJoinResult extends ClubChange {
  status: "joined" | "pending";
}

export function joinClub(clubId: string): Promise<ClubJoinResult> {
  return authedJson<ClubJoinResult>(`/api/social/clubs/${encodeURIComponent(clubId)}/join`, {});
}

export function cancelClubRequest(clubId: string): Promise<ClubChange> {
  return authedJson<ClubChange>(`/api/social/clubs/${encodeURIComponent(clubId)}/cancel`, {});
}

export interface ClubRequestList {
  requests: ClubRequestDto[];
}

export function fetchClubRequests(clubId: string): Promise<ClubRequestList> {
  return authedJson<ClubRequestList>(
    `/api/social/clubs/${encodeURIComponent(clubId)}/requests`,
  );
}

export interface ClubRequestChange {
  requests: ClubRequestDto[];
  clubs: ClubSummary[];
}

export function resolveClubRequest(
  clubId: string,
  requestId: string,
  outcome: "accept" | "reject",
): Promise<ClubRequestChange> {
  return authedJson<ClubRequestChange>(
    `/api/social/clubs/${encodeURIComponent(clubId)}/requests/${encodeURIComponent(requestId)}/${outcome}`,
    {},
  );
}

export function expelMember(clubId: string, playerId: string): Promise<PresidentChange> {
  return authedJson<PresidentChange>(
    `/api/social/clubs/${encodeURIComponent(clubId)}/expel`,
    { playerId },
  );
}

export function setClubBlurb(clubId: string, text: string): Promise<{ clubs: ClubSummary[] }> {
  return authedJson<{ clubs: ClubSummary[] }>(
    `/api/social/clubs/${encodeURIComponent(clubId)}/blurb`,
    { text },
  );
}

export interface AnnouncementList {
  announcements: ClubAnnouncementDto[];
}

export function fetchAnnouncements(clubId: string): Promise<AnnouncementList> {
  return authedJson<AnnouncementList>(
    `/api/social/clubs/${encodeURIComponent(clubId)}/announcements`,
  );
}

export function announce(clubId: string, text: string): Promise<AnnouncementList> {
  return authedJson<AnnouncementList>(
    `/api/social/clubs/${encodeURIComponent(clubId)}/announcements`,
    { text },
  );
}

export function leaveClub(clubId: string): Promise<ClubChange> {
  return authedJson<ClubChange>(`/api/social/clubs/${encodeURIComponent(clubId)}/leave`, {});
}

/** Nombrar presidente. `null` destituye y deja el club acéfalo, que es válido. */
export interface PresidentChange {
  roster: ClubRosterDto;
  clubs: ClubSummary[];
}

export function setClubPresident(
  clubId: string,
  playerId: string | null,
): Promise<PresidentChange> {
  return authedJson<PresidentChange>(
    `/api/social/clubs/${encodeURIComponent(clubId)}/president`,
    { playerId },
  );
}
